import { Merchant } from '@/__generated__/types';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

type BookingSeoHeadProps = {
    merchant?: Merchant;
    pageTitlePrefix?: string;
    pageTitleSuffix?: string;
    pageDescriptionSuffix?: string;
    shouldIndex: boolean;
};

const getMerchantDisplayName = (merchant?: Merchant): string => {
    if (!merchant) {
        return '';
    }

    return merchant.name;
};

const getDefaultDescription = (merchant?: Merchant): string => {
    if (!merchant) {
        return 'Book a table with OrderBuddy.';
    }

    const descriptionParts = [
        `Book a table at ${merchant.name}.`,
        merchant.altName ? `${merchant.altName}.` : '',
        merchant.address ? `${merchant.address}.` : '',
        merchant.phoneNumber ? `Call ${merchant.phoneNumber}.` : ''
    ].filter(Boolean);

    return descriptionParts.join(' ').trim();
};

const getOgImage = (merchant?: Merchant): string | undefined => {
    const logoImage = merchant?.logoImages?.find((image): image is string => Boolean(image));
    return logoImage || merchant?.logo || undefined;
};

const toReadableLabel = (value?: string | null): string | undefined => {
    if (!value) {
        return undefined;
    }

    return value.replace(/([a-z])([A-Z])/g, '$1 $2');
};

const createStructuredAddress = (merchant: Merchant): Record<string, unknown> => {
    if (merchant.addressObject) {
        return {
            '@type': 'PostalAddress',
            streetAddress: [merchant.addressObject.line1, merchant.addressObject.line2, merchant.addressObject.line3]
                .filter(Boolean)
                .join(', '),
            addressLocality: merchant.addressObject.city,
            addressRegion: merchant.addressObject.stateOrProvince || merchant.addressObject.stateOrProvinceCode,
            postalCode: merchant.addressObject.postalCode,
            addressCountry: merchant.addressObject.countryCode || merchant.addressObject.country
        };
    }

    return {
        '@type': 'PostalAddress',
        streetAddress: merchant.address,
        addressLocality: merchant.suburb || merchant.city,
        addressRegion: merchant.stateOrProvinceCode,
        addressCountry: 'AU'
    };
};

const createJsonLd = (
    merchant: Merchant,
    canonicalUrl: string,
    description: string,
    image?: string
): Record<string, unknown> => {
    const cuisine = toReadableLabel(merchant.restaurantCategory);
    const additionalType = toReadableLabel(merchant.restaurantType);
    const hasRestaurantSignals = Boolean(merchant.restaurantCategory || merchant.restaurantType);
    const jsonLd: Record<string, unknown> = {
        '@context': 'https://schema.org',
        '@type': hasRestaurantSignals ? 'Restaurant' : 'LocalBusiness',
        '@id': canonicalUrl,
        url: canonicalUrl,
        name: merchant.name,
        description,
        telephone: merchant.phoneNumber,
        address: createStructuredAddress(merchant)
    };

    if (merchant.altName) {
        jsonLd.alternateName = merchant.altName;
    }

    if (image) {
        jsonLd.image = image;
    }

    if (merchant.latitude != null && merchant.longitude != null) {
        jsonLd.geo = {
            '@type': 'GeoCoordinates',
            latitude: merchant.latitude,
            longitude: merchant.longitude
        };
    }

    if (cuisine) {
        jsonLd.servesCuisine = cuisine;
    }

    if (additionalType) {
        jsonLd.additionalType = additionalType;
    }

    return jsonLd;
};

const buildPageTitle = (merchantName: string, pageTitlePrefix?: string, pageTitleSuffix?: string): string => {
    const titleParts = [pageTitlePrefix, merchantName, pageTitleSuffix, 'OrderBuddy'].filter(Boolean);
    return titleParts.join(' | ');
};

const BookingSeoHead = ({
    merchant,
    pageTitlePrefix,
    pageTitleSuffix,
    pageDescriptionSuffix,
    shouldIndex
}: BookingSeoHeadProps): JSX.Element => {
    const location = useLocation();
    const merchantName = getMerchantDisplayName(merchant);
    const title = buildPageTitle(merchantName, pageTitlePrefix, pageTitleSuffix);
    const baseDescription = getDefaultDescription(merchant);
    const description = pageDescriptionSuffix ? `${baseDescription} ${pageDescriptionSuffix}`.trim() : baseDescription;
    const canonicalUrl =
        typeof window !== 'undefined' ? `${window.location.origin}${location.pathname}` : location.pathname;
    const robotsContent = shouldIndex ? 'index,follow' : 'noindex,follow';
    const ogImage = getOgImage(merchant);
    const jsonLd = merchant ? JSON.stringify(createJsonLd(merchant, canonicalUrl, description, ogImage)) : undefined;

    return (
        <Helmet>
            <title>{title}</title>
            <meta name="description" content={description} />
            <meta name="robots" content={robotsContent} />
            <link rel="canonical" href={canonicalUrl} />
            <meta property="og:type" content="website" />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={canonicalUrl} />
            {ogImage && <meta property="og:image" content={ogImage} />}
            <meta name="twitter:card" content={ogImage ? 'summary_large_image' : 'summary'} />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            {ogImage && <meta name="twitter:image" content={ogImage} />}
            {jsonLd && <script type="application/ld+json">{jsonLd}</script>}
        </Helmet>
    );
};

export default BookingSeoHead;
